import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { Header } from '@/components/Header'
import { useColors } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { Upload, FileSpreadsheet, Calendar, ArrowRight, CheckCircle, AlertCircle, Download, History } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/payroll')({
  component: PayrollPage,
})

function PayrollPage() {
  const { userToken, isAuthenticated } = useAuthStore()
  const { selectedCompany } = useCompanyStore()
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)
  
  // ───── Step 1: file upload ─────
  const [fileObj, setFileObj] = useState<File | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  // ───── Step 2: date range ─────
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null)

  // ───── Step 3: generate ─────
  const [processing, setProcessing] = useState(false)

  // ───── Previous Reports Section ─────
  const [previousReportsDateRange, setPreviousReportsDateRange] = useState<{ start: Date; end: Date } | null>(null)
  const [downloadingPrevious, setDownloadingPrevious] = useState(false)

  const handleAuthError = async (response: Response) => {
    if (response.status === 401) {
      alert('Your session has expired. Please log in again.')
      // Force redirect to login instead of using logout to avoid state issues
      window.location.href = '/login'
      return true
    }
    return false
  }

  const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
    if (!userToken || !isAuthenticated) {
      throw new Error('No authentication token available')
    }

    // Clean the token more thoroughly - remove any extra quotes, whitespace, or newlines
    const cleanToken = userToken.toString().trim().replace(/^["']|["']$/g, '').replace(/\s+/g, '')
    
    // Validate token format
    if (!cleanToken || cleanToken.length < 10) {
      throw new Error('Invalid authentication token format')
    }
    
    // Ensure we have the right headers
    let headers: Record<string, string> = {
      'Authorization': `Bearer ${cleanToken}`,
    }
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value
        })
      } else {
        headers = { ...headers, ...(options.headers as Record<string, string>) }
      }
    }

    // Only add Content-Type for JSON requests
    if (options.body && typeof options.body === 'string') {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    return response
  }

  const handleChangeFile = () => {
    setUploadSuccess(false)
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsx,.xls,.csv'
    input.onchange = e => {
      const f = (e.target as HTMLInputElement).files?.[0]
      if (f) {
        setFileObj(f)
        setFileName(f.name)
      }
    }
    input.click()
  }

  const handleUploadFile = async () => {
    if (!fileObj || !userToken || !isAuthenticated) {
      alert('Missing file or authentication token. Please try logging in again.')
      return
    }
    
    setUploading(true)
    setUploadSuccess(false)
    
    try {
      const formData = new FormData()
      formData.append('file', fileObj)
      
      // Clean the token
      const cleanToken = userToken.toString().trim().replace(/^["']|["']$/g, '').replace(/\s+/g, '')
      
      // Validate token
      if (!cleanToken || cleanToken.length < 10) {
        throw new Error('Invalid authentication token. Please log in again.')
      }
      
      const resp = await fetch(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/uploadexcel',
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${cleanToken}`,
            // Don't set Content-Type for FormData - let browser set it with boundary
          },
          body: formData,
        }
      )
      
      if (await handleAuthError(resp)) return
      
      const raw = await resp.text()
      
      const parsed = (() => {
        try { return JSON.parse(raw) }
        catch { return null }
      })()
      
      if (!resp.ok) {
        throw new Error(parsed?.message || `HTTP ${resp.status}: ${resp.statusText}`)
      }
      
      setUploadSuccess(true)
      alert('✓ Excel uploaded successfully.')
    } catch (err: any) {
      console.error('[Payroll] Upload error:', err)
      alert('Upload failed: ' + err.message)
    } finally {
      setUploading(false)
    }
  }

  const handleGenerateReport = async () => {
    if (!fileObj || !uploadSuccess || !dateRange || !userToken || !isAuthenticated) {
      alert('Please complete all steps: upload file, select date range, and ensure you are logged in.')
      return
    }

    // enforce exactly 7 days
    const days = (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24) + 1
    if (days !== 7) {
      alert('Please select exactly 7 days (inclusive).')
      return
    }

    // Use the apiName for the backend API call
    const payload = {
      Company: selectedCompany.apiName, // Use apiName instead of name
      FromDate: dateRange.start.toISOString().slice(0, 10),
      ToDate: dateRange.end.toISOString().slice(0, 10),
    }

    console.log('[Payroll] Starting report generation with payload:', payload)
    setProcessing(true)
    try {
      // 1) JSON summary - ProcessPayroll with token
      const sumResp = await makeAuthenticatedRequest(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/ProcessPayroll',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )
      
      if (await handleAuthError(sumResp)) return
      
      if (!sumResp.ok) {
        const errorText = await sumResp.text()
        console.error('[Payroll] ProcessPayroll failed:', errorText)
        throw new Error(`ProcessPayroll failed: ${errorText}`)
      }

      const sumJson = await sumResp.json()
      console.log('[Payroll] ProcessPayroll success:', sumJson)

      const { processed = [], skipped = [], failed = [] } = sumJson.data || {}
      alert(
        sumJson.message +
        `\n\n✔ Processed: ${processed.join(', ')}` +
        `\n➟ Skipped:   ${skipped.join(', ')}` +
        `\n✖ Failed:    ${failed.join(', ')}`
      )

      // 2) file download - GeneratePayrollReport with exact curl format
      console.log('[Payroll] Starting file download...')
      const fileResp = await makeAuthenticatedRequest(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/GeneratePayrollReport',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )
      
      if (await handleAuthError(fileResp)) return
      
      if (!fileResp.ok) {
        const errText = await fileResp.text()
        console.error('[Payroll] GeneratePayrollReport failed:', errText)
        
        // Try to parse as JSON to get more details
        try {
          const errorJson = JSON.parse(errText)
          throw new Error(`GeneratePayrollReport failed: ${errorJson.message || errorJson.error || errText}`)
        } catch (parseError) {
          throw new Error(`GeneratePayrollReport failed (${fileResp.status}): ${errText}`)
        }
      }
      
      // Check if response is actually a file
      const contentType = fileResp.headers.get('content-type')
      console.log('[Payroll] Response content-type:', contentType)
      
      if (contentType && contentType.includes('application/json')) {
        // It's JSON, not a file - probably an error
        const jsonResponse = await fileResp.json()
        throw new Error(`Expected file download but got JSON: ${JSON.stringify(jsonResponse)}`)
      }
      
      const blob = await fileResp.blob()
      console.log('[Payroll] Downloaded blob size:', blob.size)
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty')
      }
      
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `processed_payroll_${payload.Company.replace(/\s+/g, '_')}_${payload.FromDate}_to_${payload.ToDate}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      
      console.log('[Payroll] File download completed successfully')
      
    } catch (err: any) {
      console.error('[Payroll] Generate report error:', err)
      alert('Generate failed: ' + err.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleDownloadPreviousReport = async () => {
    if (!previousReportsDateRange || !userToken || !isAuthenticated) {
      alert('Please select a date range and ensure you are logged in.')
      return
    }

    // Use the apiName for the backend API call
    const payload = {
      Company: selectedCompany.apiName, // Use apiName instead of name
      FromDate: previousReportsDateRange.start.toISOString().slice(0, 10),
      ToDate: previousReportsDateRange.end.toISOString().slice(0, 10),
    }

    console.log('[Payroll] Downloading previous report with payload:', payload)
    setDownloadingPrevious(true)
    try {
      const fileResp = await makeAuthenticatedRequest(
        'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api/GeneratePayrollReport',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )
      
      if (await handleAuthError(fileResp)) return
      
      if (!fileResp.ok) {
        const errText = await fileResp.text()
        console.error('[Payroll] Previous report download failed:', errText)
        
        try {
          const errorJson = JSON.parse(errText)
          throw new Error(`Download failed: ${errorJson.message || errorJson.error || errText}`)
        } catch (parseError) {
          throw new Error(`Download failed (${fileResp.status}): ${errText}`)
        }
      }
      
      // Check if response is actually a file
      const contentType = fileResp.headers.get('content-type')
      console.log('[Payroll] Previous report content-type:', contentType)
      
      if (contentType && contentType.includes('application/json')) {
        const jsonResponse = await fileResp.json()
        throw new Error(`Expected file download but got JSON: ${JSON.stringify(jsonResponse)}`)
      }
      
      const blob = await fileResp.blob()
      console.log('[Payroll] Previous report blob size:', blob.size)
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty')
      }
      
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payroll_report_${payload.Company.replace(/\s+/g, '_')}_${payload.FromDate}_to_${payload.ToDate}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      
      console.log('[Payroll] Previous report download completed successfully')
      
    } catch (err: any) {
      console.error('[Payroll] Previous report download error:', err)
      alert('Download failed: ' + err.message)
    } finally {
      setDownloadingPrevious(false)
    }
  }

  const generateDisabled = !fileObj || !uploadSuccess || !dateRange || processing
  const downloadPreviousDisabled = !previousReportsDateRange || downloadingPrevious

  // Show warning if not authenticated
  if (!isAuthenticated || !userToken) {
    return (
      <div className="flex-1">
        <Header title="Payroll Report" />
        <div className="p-6">
          <div 
            className="p-6 rounded-xl text-center border"
            style={{ 
              backgroundColor: Colors.error.main + '10', 
              borderColor: Colors.error.main 
            }}
          >
            <AlertCircle size={48} color={Colors.error.main} className="mx-auto mb-4" />
            <h3 className="text-xl font-inter-semibold mb-2" style={{ color: Colors.error.main }}>
              Authentication Required
            </h3>
            <p className="font-inter-regular" style={{ color: Colors.text.secondary }}>
              You need to be logged in to access the payroll report functionality.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1">
      <Header title="Payroll Report" />

      <div className="p-6 space-y-8">
        {/* Generate New Report Section */}
        <div 
          className="max-w-4xl mx-auto p-6 rounded-2xl shadow-lg"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
              : '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <h2 className="text-xl font-inter-bold mb-6" style={{ color: Colors.text.primary }}>
            Generate New Payroll Report
          </h2>

          <div className="mb-4 p-3 rounded-md" style={{ backgroundColor: Colors.neutral[100] }}>
            <p className="text-sm font-inter-medium" style={{ color: Colors.text.primary }}>
              Company: <span className="font-inter-bold">{selectedCompany.name}</span>
            </p>
          </div>

          {/* Step 1: upload */}
          <div 
            className="pb-6 mb-6 border-b"
            style={{ borderBottomColor: Colors.border.main }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <FileSpreadsheet size={24} color={Colors.primary.main} />
              <h3 className="text-lg font-inter-semibold" style={{ color: Colors.text.primary }}>
                1. Upload Raw File (≤ 1 MB)
              </h3>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleChangeFile}
                disabled={uploading || processing}
                className="flex items-center space-x-2 px-4 py-2 rounded-md font-inter-medium text-white disabled:opacity-50"
                style={{ backgroundColor: Colors.primary.main }}
              >
                <Upload size={20} />
                <span>{fileName ? 'Change File' : 'Select File'}</span>
              </button>
              
              {fileObj && (
                <button
                  onClick={handleUploadFile}
                  disabled={uploading || processing}
                  className="flex items-center space-x-2 px-4 py-2 rounded-md font-inter-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: uploading ? '#777' : '#28A745' }}
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <span>Upload File</span>
                  )}
                </button>
              )}
              
              {uploadSuccess && (
                <div 
                  className="flex items-center space-x-1 px-3 py-1 rounded-md"
                  style={{ backgroundColor: '#28A745' }}
                >
                  <CheckCircle size={16} color="#FFFFFF" />
                  <span className="text-sm font-inter-semibold text-white">✓ Uploaded</span>
                </div>
              )}
            </div>
            
            {fileName && (
              <p className="text-sm font-inter-regular mt-2" style={{ color: Colors.text.secondary }}>
                Selected: {fileName}
              </p>
            )}
          </div>

          {/* Step 2: date */}
          <div 
            className="pb-6 mb-6 border-b"
            style={{ borderBottomColor: Colors.border.main }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <Calendar size={24} color={Colors.primary.main} />
              <h3 className="text-lg font-inter-semibold" style={{ color: Colors.text.primary }}>
                2. Select Date Range (Must be exactly 7 days)
              </h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="block text-sm font-inter-medium mb-1" style={{ color: Colors.text.primary }}>
                  Start Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border rounded-md font-inter-regular"
                  style={{ 
                    borderColor: Colors.border.main,
                    backgroundColor: Colors.background.default,
                    color: Colors.text.primary,
                  }}
                  onChange={(e) => {
                    const start = new Date(e.target.value)
                    if (dateRange?.end) {
                      setDateRange({ start, end: dateRange.end })
                    } else {
                      setDateRange({ start, end: start })
                    }
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-inter-medium mb-1" style={{ color: Colors.text.primary }}>
                  End Date
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border rounded-md font-inter-regular"
                  style={{ 
                    borderColor: Colors.border.main,
                    backgroundColor: Colors.background.default,
                    color: Colors.text.primary,
                  }}
                  onChange={(e) => {
                    const end = new Date(e.target.value)
                    if (dateRange?.start) {
                      setDateRange({ start: dateRange.start, end })
                    } else {
                      setDateRange({ start: end, end })
                    }
                  }}
                />
              </div>
            </div>

            {dateRange && (
              <div className="mt-3">
                <p className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                  Selected range: {Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                </p>
              </div>
            )}
          </div>

          {/* Step 3: generate */}
          <div>
            <button
              onClick={handleGenerateReport}
              disabled={generateDisabled}
              className="flex items-center space-x-2 px-6 py-3 rounded-md font-inter-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: Colors.primary.main }}
            >
              {processing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <span>Generate Report</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>

          {/* Status Messages */}
          {generateDisabled && !processing && (
            <div 
              className="flex items-center space-x-2 p-3 mt-4 rounded-md"
              style={{ backgroundColor: Colors.neutral[100] }}
            >
              <AlertCircle size={16} color={Colors.text.secondary} />
              <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                Complete all steps above to generate your payroll report
              </span>
            </div>
          )}
        </div>

        {/* Download Previous Reports Section */}
        <div 
          className="max-w-4xl mx-auto p-6 rounded-2xl shadow-lg"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
              : '0 4px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div className="flex items-center space-x-3 mb-6">
            <History size={24} color={Colors.primary.main} />
            <h2 className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
              Download Previous Reports
            </h2>
          </div>

          <div className="mb-4 p-3 rounded-md" style={{ backgroundColor: Colors.neutral[100] }}>
            <p className="text-sm font-inter-medium" style={{ color: Colors.text.primary }}>
              Company: <span className="font-inter-bold">{selectedCompany.name}</span>
            </p>
          </div>

          <div className="mb-4">
            <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
              Download previously generated payroll reports for <strong>{selectedCompany.name}</strong> by selecting a date range.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-md mb-6">
            <div>
              <label className="block text-sm font-inter-medium mb-1" style={{ color: Colors.text.primary }}>
                Start Date
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{ 
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
                onChange={(e) => {
                  const start = new Date(e.target.value)
                  if (previousReportsDateRange?.end) {
                    setPreviousReportsDateRange({ start, end: previousReportsDateRange.end })
                  } else {
                    setPreviousReportsDateRange({ start, end: start })
                  }
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-inter-medium mb-1" style={{ color: Colors.text.primary }}>
                End Date
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{ 
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
                onChange={(e) => {
                  const end = new Date(e.target.value)
                  if (previousReportsDateRange?.start) {
                    setPreviousReportsDateRange({ start: previousReportsDateRange.start, end })
                  } else {
                    setPreviousReportsDateRange({ start: end, end })
                  }
                }}
              />
            </div>
          </div>

          <button
            onClick={handleDownloadPreviousReport}
            disabled={downloadPreviousDisabled}
            className="flex items-center space-x-2 px-6 py-3 rounded-md font-inter-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: Colors.secondary.main }}
          >
            {downloadingPrevious ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Download size={20} />
                <span>Download Previous Report</span>
              </>
            )}
          </button>

          {downloadPreviousDisabled && !downloadingPrevious && (
            <div 
              className="flex items-center space-x-2 p-3 mt-4 rounded-md"
              style={{ backgroundColor: Colors.neutral[100] }}
            >
              <AlertCircle size={16} color={Colors.text.secondary} />
              <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                Please select a date range to download previous reports
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}